"use client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { SignInDialogWithTrigger } from "@/components/auth/sign-in-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { Ingredient } from "@/lib/types";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { CreateIngredientForm } from "./forms";
import {
  CreateIngredientFormData,
  convertFormDataToIngredientPayload,
} from "./forms/schema";
import { useIngredientForm } from "./useIngredientForm";
import { cn } from "@/lib/utils";
import {
  useCreateIngredientForm,
  categories,
  type FormMode,
  getCategoryDefaults,
  CATEGORY_DEFAULT_FIELDS,
} from "./useCreateIngredientForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useMutation } from "@tanstack/react-query";
import { createIngredient } from "@/app/actions/ingredients/createIngredient";
import { toast } from "sonner";
import { useSession } from "@/lib/auth-client";
import { useEffect, useState } from "react";
import { trackEvent } from "@/lib/analytics";
import { useIsClient } from "@/lib/hooks/useIsClient";
type NewIngredientDialogProps = {
  children: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onIngredientCreated?: (ingredient: Ingredient) => void;
  initialName?: string;
  onCreate?: () => void;
};
const MODE_LABELS = {
  simple: {
    label: "Simple",
    description: "Estimate ingredient composition",
  },
  nutrition: {
    label: "Nutrition Facts",
    description: "Enter values from a nutrition label",
  },
  advanced: {
    label: "Advanced",
    description: "Enter all composition values directly",
  },
} satisfies Record<FormMode, { label: string; description: string }>;
export function NewIngredientDialog({
  children,
  onIngredientCreated,
  initialName,
  onCreate,
}: NewIngredientDialogProps) {
  const isClient = useIsClient();
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const {
    selectedCategory,
    formMode,
    availableModes,
    selectCategory,
    selectMode,
    reset,
  } = useCreateIngredientForm();
  const { mutate: createIngredientMutation, isPending } = useMutation({
    mutationFn: createIngredient,
    onSuccess: (data) => {
      onIngredientCreated?.(data);
      reset();
      form.reset();
      setOpen(false);
      onCreate?.();
      toast.success(`Added ingredient: ${data.name}`);
    },
    onError: (error) => {
      toast.error("Failed to create ingredient", {
        description: error.message,
      });
    },
  });
  const form = useIngredientForm({
    name: initialName || "",
  });
  useEffect(() => {
    if (initialName) {
      form.setValue("name", initialName);
    }
  }, [initialName, form]);
  // Trigger revalidation when form mode or category changes to clear any stale validation errors
  useEffect(() => {
    // Clear all errors and revalidate composition fields
    form.clearErrors();
    form.trigger([
      "composition",
      "waterPct",
      "fatPct",
      "sugarsPct",
      "alcoholPct",
    ]);
    form.clearErrors("root");
  }, [formMode, selectedCategory, form]);
  const handleIngredientSubmit = async (data: CreateIngredientFormData) => {
    if (!selectedCategory) return;
    trackEvent("calculator-new-ingredient-submit", {
      ["ingredient-name"]: data.name,
      ["ingredient-category"]: selectedCategory,
      ["form-mode"]: formMode ?? "",
    });
    createIngredientMutation({
      data: {
        ...convertFormDataToIngredientPayload(data),
        category: selectedCategory,
      },
    });
  };
  // Keep server and initial client output identical to prevent hydration
  // mismatches when session state resolves on the client.
  if (!isClient) {
    return children;
  }
  // If user is not authenticated, show sign-in dialog instead
  if (!session?.user) {
    return (
      <SignInDialogWithTrigger analyticsFrom="new-ingredient">
        {children}
      </SignInDialogWithTrigger>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={children}></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create a new ingredient</DialogTitle>
          <DialogDescription>
            If you can&apos;t find the ingredient you&apos;re looking for, use
            this form to add it to your personal database.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              form.handleSubmit(handleIngredientSubmit)(e);
            }}
            className="space-y-4"
            id="create-ingredient-form"
          >
            {/* Ingredient Name - Always at the top */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      autoFocus
                      placeholder="e.g., Heavy Cream"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Category Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Category</Label>
              <Select
                value={selectedCategory || ""}
                onValueChange={(value) => {
                  const category = categories.find(
                    (candidate) => candidate.value === value,
                  );
                  if (!category) {
                    return;
                  }
                  selectCategory(category.value);
                  const categoryDefaults = getCategoryDefaults(category.value);
                  for (const field of CATEGORY_DEFAULT_FIELDS) {
                    const defaultValue = categoryDefaults[field];
                    if (defaultValue !== undefined) {
                      form.setValue(field, defaultValue);
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mode Selection - only visible when category is selected */}
            {selectedCategory && (
              <div
                className={cn(
                  "space-y-2 transition-all duration-200",
                  !selectedCategory && "pointer-events-none opacity-50",
                )}
              >
                <Label className="text-sm font-medium">Entry Mode</Label>
                <RadioGroup
                  value={formMode || ""}
                  onValueChange={(value) => {
                    const mode = availableModes.find(
                      (candidate) => candidate === value,
                    );
                    if (mode) {
                      selectMode(mode);
                    }
                  }}
                  className={cn("flex-col gap-2")}
                >
                  {availableModes.map((mode) => (
                    <Label
                      key={mode}
                      htmlFor={`mode-${mode}`}
                      className={cn(
                        "interactive hover:bg-accent flex cursor-pointer items-start gap-1 space-x-2 rounded-lg border px-3 py-2.5 transition-colors",
                      )}
                    >
                      <RadioGroupItem value={mode} id={`mode-${mode}`} />
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {MODE_LABELS[mode].label}
                        </span>
                        <span className="text-muted-foreground text-xs font-normal">
                          {MODE_LABELS[mode].description}
                        </span>
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Form Fields - visible when both category and mode are selected */}
            {selectedCategory && formMode && (
              <div className="space-y-2">
                <CreateIngredientForm
                  form={form}
                  formMode={formMode}
                  selectedCategory={selectedCategory}
                />
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes (Optional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Any additional notes about this ingredient"
                          rows={3}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </form>
        </Form>

        <DialogFooter className="flex-row justify-between">
          {selectedCategory && formMode && (
            <Button
              type="submit"
              form="create-ingredient-form"
              busy={isPending}
              disabled={!!form.formState.errors.root}
            >
              Create ingredient
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
